import { Picture } from '../models/Picture';

type PictureCollection = Record<string, Picture> | Picture[] | undefined | null;

const picturesAreEqual = (a: Picture | undefined, b: Picture | undefined): boolean => {
  if (!a || !b) {
    return false;
  }

  if (a.id !== undefined && b.id !== undefined) {
    return a.id === b.id;
  }

  return a.large === b.large || a.thumb === b.thumb;
};

const cloneCollection = (collection: PictureCollection): Record<string, Picture> | Picture[] | undefined => {
  if (Array.isArray(collection)) {
    return [...collection];
  }

  if (collection && typeof collection === 'object') {
    return { ...collection } as Record<string, Picture>;
  }

  return undefined;
};

/**
 * Merge existing and incoming conversation picture collections and ensure a new picture
 * is available immediately without requiring a full reload.
 */
export const mergeConversationPictures = (
  currentPictures: PictureCollection,
  incomingPictures: PictureCollection,
  newPicture?: Picture | null
): Record<string, Picture> | Picture[] | undefined => {
  const baseCollection = incomingPictures ?? currentPictures;
  const isArrayCollection = Array.isArray(baseCollection) || (!baseCollection && Array.isArray(currentPictures));

  if (isArrayCollection) {
    const result = (Array.isArray(baseCollection)
      ? cloneCollection(baseCollection)
      : cloneCollection(currentPictures)) as Picture[] | undefined;

    if (!result) {
      if (!newPicture) {
        return undefined;
      }
      return [newPicture];
    }

    if (newPicture && !result.some(existing => picturesAreEqual(existing, newPicture))) {
      result.push(newPicture);
    }

    return result;
  }

  const resultObject = (baseCollection && typeof baseCollection === 'object'
    ? (cloneCollection(baseCollection) as Record<string, Picture>)
    : (cloneCollection(currentPictures) as Record<string, Picture> | undefined)) ?? {};

  if (newPicture) {
    const existingEntry = Object.entries(resultObject).find(([, picture]) => picturesAreEqual(picture, newPicture));
    const key = existingEntry ? existingEntry[0] : String(newPicture.id ?? newPicture.large ?? newPicture.thumb ?? Object.keys(resultObject).length);
    resultObject[key] = newPicture;
  }

  return resultObject;
};
